"use client"

import React, { useState, useEffect, useMemo, useRef } from "react"
import {
  Avatar,
  Badge,
  Button,
  Progress,
  Breadcrumb,
  Tag,
  Pagination,
  Spin,
  Dropdown,
  Menu,
  DatePicker,
  Modal,
} from "antd"
import { useNavigate } from "react-router-dom"
import {
  StarOutlined,
} from "@ant-design/icons"
import "bootstrap/dist/css/bootstrap.min.css"
import dayjs from "dayjs"
import BarContainer from "./BarContainer"
import GenerationThreadModal from "./Modals/GenerationThreadModal"
import SketchToImgModal from "./Modals/SketchToImgModal"
import TextToImgModal from "./Modals/TextToImgModal"
import QuickEditsModal from "./Modals/QuickEditsModal"
import FurnitureFinderModal from "./Modals/FurnitureFinderModal"
import VirtualUnstagingModal from "./Modals/VirtualUnstagingModal"
import InputOutputFeatureModal from "./Modals/InputOutputFeatureModal"
import { Analytics } from "../../Apis/analyticsApi"
import { message, ConfigProvider } from "antd"
import {
  formatFeatureLabel,
  formatFloorGenerationImageKey,
  formatRoomPlannerOutputLabel,
  getRoomPlannerCacheKey,
  roomPlannerNamesMatch,
  extractRoomNameFromTimelineDetail,
  parseRecentGenerations,
  buildRecentGenerationStubsFromTimeline,
  mergeRecentGenerations,
  applyTimelineTimestampsToRecentGenerations,
  normalizeFeatureKey,
  resolveTimelineFeatureKey,
  fetchRoomPlannerDetail,
  getRoomPlannerModalGridLayout,
  getRoomPlannerModalImageStyle,
  getRoomPlannerThumbnailLayout,
  matchesFloorDesignTitle,
  getFeatureDashboardIcon,
} from "../../utils/organizationApiHelpers"

// The user_detail payload has moved the plan image counters around between
// endpoint versions: they can sit on the envelope, on results[0], or inside a
// nested subscription row (where a null on the outer object shadows the real
// value). Resolve them by scanning the whole payload for the first usable
// number, in key priority order.
const PLAN_TOTAL_IMAGE_KEYS = [
  "current_plan_total_images",
  "plan_total_images",
  "total_credits",
  "total_images",
]

const PLAN_USED_IMAGE_KEYS = [
  "current_plan_used_images",
  "plan_used_images",
  "overall_used_images",
  "used_credits",
  "credit_usage",
]

const collectPayloadNodes = (source, maxDepth = 6, maxNodes = 800) => {
  const nodes = []
  const queue = [[source, 0]]

  while (queue.length > 0 && nodes.length < maxNodes) {
    const [node, depth] = queue.shift()
    if (!node || typeof node !== "object" || depth > maxDepth) continue

    if (Array.isArray(node)) {
      node.forEach((item) => queue.push([item, depth + 1]))
      continue
    }

    nodes.push(node)
    Object.values(node).forEach((value) => {
      if (value && typeof value === "object") queue.push([value, depth + 1])
    })
  }

  return nodes
}

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null
  const num = Number(String(value).replace(/[^0-9.-]/g, ""))
  return Number.isFinite(num) ? num : null
}

const findCounterMatches = (nodes, keys) => {
  const matches = []

  keys.forEach((key) => {
    nodes.forEach((node) => {
      if (!(key in node)) return
      const value = toFiniteNumber(node[key])
      if (value !== null) matches.push({ key, value, node })
    })
  })

  return matches
}

const resolvePlanCounters = (source) => {
  const nodes = collectPayloadNodes(source)
  const totalMatches = findCounterMatches(nodes, PLAN_TOTAL_IMAGE_KEYS)
  const usedMatches = findCounterMatches(nodes, PLAN_USED_IMAGE_KEYS)

  // A plan total of 0 is virtually always a placeholder on an outer/stale
  // object shadowing the real quota deeper in the payload, so take the first
  // positive match and only fall back to a zero when nothing else exists.
  const total =
    totalMatches.find((match) => match.value > 0) || totalMatches[0] || null

  // Read "used" from the same object that supplied the total whenever possible
  // so the two numbers describe the same plan; 0 used images is legitimate.
  const used =
    (total && usedMatches.find((match) => match.node === total.node)) ||
    usedMatches.find((match) => match.value > 0) ||
    usedMatches[0] ||
    null

  if (process.env.NODE_ENV !== "production" && source) {
    console.log("[user_detail] RESOLVE plan counters", {
      total: total && `${total.key}=${total.value}`,
      used: used && `${used.key}=${used.value}`,
      totalCandidates: totalMatches.map((m) => `${m.key}=${m.value}`),
      usedCandidates: usedMatches.map((m) => `${m.key}=${m.value}`),
    })
  }

  return {
    total: total ? total.value : null,
    used: used ? used.value : null,
  }
}

// Same shadowing problem as the plan counters: the generated-images total sits
// on a different node depending on the endpoint version, and a 0 (or a missing
// key) on the outer object hides the real number. Scan the whole envelope in
// key-priority order, then fall back to summing the per-feature map.
const GENERATED_IMAGE_TOTAL_KEYS = [
  "total_generated_images",
  "total_images_generated",
  "generated_images_count",
  "overall_generated_images",
  "total_generations",
]

const GENERATED_IMAGE_MAP_KEYS = [
  "generated_images",
  "generated_images_count",
  "generated_images_by_feature",
]

const findGeneratedImageMap = (nodes) => {
  for (const node of nodes) {
    for (const key of GENERATED_IMAGE_MAP_KEYS) {
      const map = node[key]
      if (!map || typeof map !== "object" || Array.isArray(map)) continue
      const values = Object.values(map)
      if (values.some((value) => (toFiniteNumber(value) || 0) > 0)) return map
    }
  }
  return null
}

const resolveGeneratedImages = (source) => {
  const nodes = collectPayloadNodes(source)
  const matches = findCounterMatches(nodes, GENERATED_IMAGE_TOTAL_KEYS)
  const direct = matches.find((match) => match.value > 0) || matches[0] || null
  const byFeature = findGeneratedImageMap(nodes)

  let total = direct && direct.value > 0 ? direct.value : null
  if (total === null && byFeature) {
    const summed = Object.values(byFeature).reduce(
      (acc, value) => acc + (toFiniteNumber(value) || 0),
      0
    )
    if (summed > 0) total = summed
  }
  if (total === null && direct) total = direct.value

  if (process.env.NODE_ENV !== "production" && source) {
    console.log("[user_detail] RESOLVE generated images", {
      total,
      candidates: matches.map((m) => `${m.key}=${m.value}`),
      byFeatureKeys: byFeature ? Object.keys(byFeature) : null,
    })
  }

  return { total, byFeature }
}

const TIMELINE_INPUT_OUTPUT_BLUE_ICONS = {
  furniture_replace: "/dashboard/replace-furniture-blue.svg",
  rearrange_furniture: "/dashboard/rearrange-furniture-blue.svg",
  furniture_design: "/dashboard/furniture-design-icon.svg",
}

const { RangePicker } = DatePicker

const UserDashboard = ({ userId, onBack }) => {
  const lang = localStorage.getItem("language") || "ar"
  const EVENT_BOOTH_TIMELINE_ICON = "/dashboard/event-booth-timeline.svg"

  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [sketchModal, setSketchModal] = useState(false)
  const [textToImgModal, setTextToImgModal] = useState(false)
  const [quickEditsModal, setQuickEditsToImgModal] = useState(false)
  const [furnitureModal, setFurnitureModal] = useState(false)
  const [virtualUnstagingModal, setVirtualUnstagingModal] = useState(false)
  const [inputOutputFeatureModal, setInputOutputFeatureModal] = useState(false)
  const [imageModal, setImageModal] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedImageLabel, setSelectedImageLabel] = useState(null)
  const [selectedImageKey, setSelectedImageKey] = useState(null)
  const [imageLoadError, setImageLoadError] = useState(false)
  const [floorGenerationModal, setFloorGenerationModal] = useState(false)
  const [selectedFloorGeneration, setSelectedFloorGeneration] = useState(null)
  const [userDetails, setUserDetails] = useState(null)
  // Raw (unmerged) user_detail payload, kept so the plan counters can be read
  // from wherever the API puts them.
  const [userDetailsRaw, setUserDetailsRaw] = useState(null)
  const [subPage, setSubPage] = useState(1)
  const [subResults, setSubResults] = useState([])
  const [subTotal, setSubTotal] = useState(0)
  const [subLoading, setSubLoading] = useState(false)
  const SUB_PAGE_SIZE = 10
  const [page, setPage] = useState(1)
  const [totalTimeline, setTotalTimeline] = useState(0)
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [timelineItems, setTimelineItems] = useState([])
  const [countryInfo, setCountryInfo] = useState({ name: "", flag: "" })

  // 🔽 Filters
  const [selectedActivityKey, setSelectedActivityKey] = useState("all")
  const [selectedDateKey, setSelectedDateKey] = useState("last30")
  const [customRange, setCustomRange] = useState(null)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [selectedTimelineItem, setSelectedTimelineItem] = useState(null)

  const activityTranslations = {
    en: {
      all: "All Activities",
      generation: "Generations",
      payment: "Payments",
      detection: "Detections",
      room_planner: "Room Planner",
    },
    ar: {
      all: "جميع الأنشطة",
      generation: "الإنشاءات",
      payment: "المدفوعات",
      detection: "الاكتشافات",
      room_planner: "مخطط الغرفة",
    },
  }

  const dateTranslations = {
    en: {
      last7: "Last 7 Days",
      last30: "Last 30 Days",
      last90: "Last 90 Days",
      custom: "Custom Range",
    },
    ar: {
      last7: "آخر 7 أيام",
      last30: "آخر 30 يومًا",
      last90: "آخر 90 يومًا",
      custom: "نطاق مخصص",
    },
  }

  useEffect(() => {
    const fetchCountry = async () => {
      if (!userDetails?.country) return

      try {
        const code = userDetails.country.toUpperCase()
        const res = await fetch(`https://restcountries.com/v3.1/alpha/${code}`)
        const data = await res.json()

        if (data && data[0]) {
          setCountryInfo({
            name: data[0].name.common,
            flag: data[0].flags.svg, // can also use png if you prefer
          })
        }
      } catch (err) {
        console.error("❌ Error fetching country info:", err)
        setCountryInfo({ name: userDetails.country, flag: "" })
      }
    }

    fetchCountry()
  }, [userDetails?.country])

  // 🔎 Dev-only: distinguishes "payload changed" from "component remounted"
  // when the Summary counters reset. Stripped from production builds.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return undefined
    console.log("[user_detail] MOUNT userId =", userId)
    return () => console.log("[user_detail] UNMOUNT userId =", userId)
  }, [userId])

  useEffect(() => {
    // Guards against a second (StrictMode / re-run) response landing after the
    // first and overwriting good data with an empty or out-of-order payload.
    let cancelled = false

    const fetchDetails = async () => {
      try {
        setLoading(true)
        const res = await Analytics.getUserDetail(userId)
        if (cancelled) return

        // ✅ Handles both axios style { data: { results: [...] } } and direct results
        const envelope = res?.data ?? res
        const row = envelope?.results?.[0]
        // Some fields (plan image counters) live on the envelope rather than on
        // the row, so merge the two with the row winning on conflicts.
        const detail = row ? { ...envelope, ...row } : envelope

        if (!detail || typeof detail !== "object") {
          console.warn("⚠️ [user_detail] ignoring empty response:", res)
          return
        }

        if (process.env.NODE_ENV !== "production") {
          console.log("[user_detail] PAYLOAD stored", {
            envelopeKeys: Object.keys(envelope || {}),
            rowKeys: Object.keys(row || {}),
          })
        }

        setUserDetailsRaw(envelope)
        setUserDetails(detail)
        // Seed subscription-history pagination from the first page.
        setSubResults(detail?.subscription_history?.results || [])
        setSubTotal(detail?.subscription_history?.count || 0)
        setSubPage(1)
      } catch (err) {
        if (!cancelled) console.error("❌ Error fetching user details:", err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    if (userId) fetchDetails()

    return () => {
      cancelled = true
    }
  }, [userId])

  // 🧾 Fetch a specific page of subscription history (server-side).
  const fetchSubscriptions = async (nextPage) => {
    if (!userId) return
    try {
      setSubLoading(true)
      const res = await Analytics.getUserDetail(userId, nextPage)
      const detail =
        res?.data?.results?.[0] || res?.results?.[0] || res?.data || res
      const sh = detail?.subscription_history
      setSubResults(sh?.results || [])
      setSubTotal(sh?.count ?? subTotal)
      setSubPage(nextPage)
    } catch (err) {
      console.error("❌ Error fetching subscription page:", err)
    } finally {
      setSubLoading(false)
    }
  }

  // 🧩 Fetch timeline
  // 🧩 Fetch timeline (auto-refreshes on filters/pagination)
  // Update the fetchTimeline useEffect to use keys:
  useEffect(() => {
    if (!userId) return

    const fetchTimeline = async () => {
      try {
        setTimelineLoading(true)

        // Map key → API param (always in English)
        let filterType = "last90days"
        if (selectedDateKey === "last7") filterType = "last7days"
        else if (selectedDateKey === "last30") filterType = "last30days"
        else if (selectedDateKey === "custom") filterType = "customdate"

        const params = {
          userId,
          filterType,
          activityType:
            selectedActivityKey === "all" ? "" : selectedActivityKey, // Send: generation, payment, detection
        }

        if (filterType === "customdate" && customRange) {
          params.startDate = customRange.startDate
          params.endDate = customRange.endDate
        }

        const res = await Analytics.getUserTimeline({
          ...params,
          page,
        })
        const data = res?.data?.results || res?.results || []
        setTimelineItems(data)
        setTotalTimeline(res?.data?.count || res?.count || data.length)
      } catch (err) {
        console.error("❌ Error fetching timeline:", err)
      } finally {
        setTimelineLoading(false)
      }
    }

    if (
      selectedDateKey !== "custom" ||
      (selectedDateKey === "custom" && customRange)
    ) {
      fetchTimeline()
    }
  }, [userId, selectedActivityKey, selectedDateKey, customRange, page])

  const activityMenu = (
    <Menu
      onClick={(e) => {
        setSelectedActivityKey(e.key)
        setPage(1)
      }}
      items={[
        { key: "all", label: activityTranslations[lang].all },
        { key: "generation", label: activityTranslations[lang].generation },
        { key: "payment", label: activityTranslations[lang].payment },
        { key: "detection", label: activityTranslations[lang].detection },
        { key: "room_planner", label: activityTranslations[lang].room_planner },
      ]}
    />
  )

  const dateMenu = (
    <Menu
      onClick={(e) => {
        setSelectedDateKey(e.key)
        setPage(1)
        if (e.key === "custom") {
          setShowDatePicker(true)
        } else {
          setShowDatePicker(false)
          setCustomRange(null)
        }
      }}
      items={[
        { key: "last7", label: dateTranslations[lang].last7 },
        { key: "last30", label: dateTranslations[lang].last30 },
        { key: "last90", label: dateTranslations[lang].last90 },
        { key: "custom", label: dateTranslations[lang].custom },
      ]}
    />
  )

  const statCards = [
    {
      label: lang === "ar" ? "إجمالي الإنفاق" : "Total Spendings",
      value: userDetails?.total_revenue_spend,
    },
    {
      label: lang === "ar" ? "الاشتراكات" : "Subscriptions",
      value: userDetails?.total_subscriptions,
    },
    {
      label: lang === "ar" ? "آخر نشاط" : "Last Active",
      value:
        lang === "ar"
          ? userDetails?.last_activity_arabic
          : userDetails?.last_activity,
    },
  ]

  const planCounters = useMemo(
    // Scan both states: `userDetailsRaw` holds the envelope, `userDetails` the
    // merged row. Which one carries the counters varies with the endpoint
    // version, and the feature bar proves `userDetails` can hold data while the
    // envelope scan comes up empty. collectPayloadNodes walks arrays, so
    // passing both is a single sweep.
    () => resolvePlanCounters([userDetailsRaw, userDetails]),
    [userDetailsRaw, userDetails]
  )

  // A later payload that carries no counters at all (failed refresh, partial
  // response) must not blank the card back to 0 / 0 after it has rendered real
  // numbers, so remember the last payload that actually had them.
  const lastPlanCountersRef = useRef({ used: 0, total: 0 })
  if (planCounters.used !== null || planCounters.total !== null) {
    lastPlanCountersRef.current = {
      used: planCounters.used ?? lastPlanCountersRef.current.used,
      total: planCounters.total ?? lastPlanCountersRef.current.total,
    }
  } else if (userDetailsRaw && lastPlanCountersRef.current.total > 0) {
    console.warn(
      "⚠️ [user_detail] payload without plan image counters:",
      Object.keys(userDetailsRaw)
    )
  }

  const planUsedImages = planCounters.used ?? lastPlanCountersRef.current.used
  const planTotalImages =
    planCounters.total ?? lastPlanCountersRef.current.total
  const planUsagePercent =
    planTotalImages > 0
      ? Math.min((planUsedImages / planTotalImages) * 100, 100)
      : 0

  const generatedImages = useMemo(
    () => resolveGeneratedImages([userDetailsRaw, userDetails]),
    [userDetailsRaw, userDetails]
  )

  // Same guard as the plan counters: a partial refresh that carries no
  // generated-images data must not blank a total that already rendered.
  const lastGeneratedTotalRef = useRef(0)
  if (generatedImages.total !== null) {
    lastGeneratedTotalRef.current = generatedImages.total
  }
  const totalGeneratedImages =
    generatedImages.total ?? lastGeneratedTotalRef.current

  if (process.env.NODE_ENV !== "production") {
    console.log("[user_detail] RENDER", {
      hasRaw: !!userDetailsRaw,
      hasDetails: !!userDetails,
      planUsedImages,
      planTotalImages,
      totalGeneratedImages,
      barKeys: Object.keys(userDetails?.generated_images_percent || {}).length,
    })
  }

  // const processedImages = [
  //   "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=300&h=300&fit=crop",
  //   "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=300&h=300&fit=crop",
  //   "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=300&h=300&fit=crop",
  //   "https://images.unsplash.com/photo-1615529328331-f8917597711f?w=300&h=300&fit=crop",
  // ]

  // const timelineItems = [
  //   {
  //     icon: <UserOutlined />,
  //     title: "New User Signup",
  //     desc: "Account created successfully",
  //     date: "2024-01-15",
  //     type: "info",
  //   },
  //   {
  //     icon: <BulbOutlined />,
  //     title: "Used Room Redesign",
  //     desc: "Generated 3 room designs using 25 credits",
  //     date: "2024-03-02",
  //     generations: 3,
  //   },
  //   {
  //     icon: <PictureOutlined />,
  //     title: "Used Text to Image",
  //     desc: "Generated AI images from text prompts",
  //     date: "2024-03-05",
  //     generations: 3,
  //   },
  //   {
  //     icon: <SearchOutlined />,
  //     title: "Used Furniture Finder",
  //     desc: "Image processed, detected 7 objects",
  //     date: "2024-03-08",
  //   },
  //   {
  //     icon: <EditOutlined />,
  //     title: "Used Quick Edits",
  //     desc: "Performed 2 quick edits using 25 credits",
  //     date: "2024-03-10",
  //     generations: 3,
  //   },
  //   {
  //     icon: <BgColorsOutlined />,
  //     title: "Used Sketch to Image",
  //     desc: "Generated 3 room designs using 25 credits",
  //     date: "2024-03-12",
  //     generations: 3,
  //   },
  //   {
  //     icon: <StarOutlined />,
  //     title: "Plan Upgraded",
  //     desc: "Upgraded to Pro plan - $29.99",
  //     date: "2024-03-15",
  //     type: "warning",
  //   },
  //   {
  //     icon: <CloseCircleOutlined />,
  //     title: "Payment Declined",
  //     desc: "Payment failed to process",
  //     date: "2024-03-18",
  //     type: "warning",
  //   },
  // ]

  // 🔹 Helper: Icon mapper
  // 🔹 Helper: Icon mapper with background color
  const getIconWithBg = (item) => {
    const title = `${item?.title || ""} ${item?.title_arabic || ""}`
    const timelineFeatureKey = resolveTimelineFeatureKey(item)
    const timelineBlueIcon = TIMELINE_INPUT_OUTPUT_BLUE_ICONS[timelineFeatureKey]
    let icon = null
    let bgColor = "#f5f5f5" // default gray background

    if (timelineBlueIcon) {
      icon = (
        <img
          src={timelineBlueIcon}
          alt={formatFeatureLabel(timelineFeatureKey, lang)}
        />
      )
      bgColor = "#E7FAFF"
    } else if (title?.includes("Enhance Image")) {
      icon = (
        <img
          src="/dashboard/image-enhannce.svg"
          alt="Enhance Image"
        />
      )
      bgColor = "#E7FAFF"
    } else if (title?.includes("Furniture Finder")) {
      icon = (
        <img
          src="/dashboard/sofa-blue.svg"
          alt="Furniture Finder"
        />
      )
      bgColor = "#E7FAFF"
    } else if (title?.includes("Text to Image")) {
      icon = (
        <img
          src="/dashboard/comment-blue.svg"
          alt="Text to Image"
        />
      )
      bgColor = "#E7FAFF"
    } else if (title?.includes("Sketch")) {
      icon = (
        <img
          src="/dashboard/sketch-to-img-blue.svg"
          alt="Sketch to Image"
        />
      )
      bgColor = "#E7FAFF"
    } else if (title?.includes("Used Furniture Removal")) {
      icon = (
        <img
          src="/dashboard/virtual-unstaging-green.svg"
          alt="Furniture Removal"
        />
      )
      bgColor = "#E7FAFF"
    } else if (title?.includes("Quick Edit")) {
      icon = (
        <img
          src="/dashboard/quick-edits-blue.svg"
          alt="Quick Edits"
        />
      )
      bgColor = "#E7FAFF"
    } else if (
      title?.toLowerCase().includes("expert design") ||
      title?.includes("expert_design") ||
      title?.includes("تصميم الخبير") ||
      title?.includes("بوث الفعاليات")
    ) {
      icon = (
        <img
          src={EVENT_BOOTH_TIMELINE_ICON}
          alt="Event Booth"
          style={{
            maxWidth: "22px",
            maxHeight: "22px",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            display: "block",
          }}
        />
      )
      bgColor = "#E7FAFF"
    } else if (title?.includes("Redesign")) {
      icon = (
        <img
          src="/dashboard/room_redesign_blue.svg"
          alt="Room Redesign"
        />
      )
      bgColor = "#E7FAFF"
    } else if (
      title?.includes("Reference Style") ||
      title?.includes("نمط المرجع")
    ) {
      icon = (
        <img
          src="/dashboard/reference-style-icon.svg"
          alt="Reference Style"
        />
      )
      bgColor = "#E7FAFF"
    } else if (
      title?.includes("Design With Product") ||
      title?.includes("التصميم بالمنتجات")
    ) {
      icon = (
        <img
          src="/dashboard/design-with-product-icon.svg"
          alt="Design With Product"
        />
      )
      bgColor = "#E7FAFF"
    } else if (
      title?.includes("Product Visualizer") ||
      title?.includes("Replicate Design") ||
      title?.includes("استنساخ التصميم")
    ) {
      icon = (
        <img
          src="/dashboard/replicate-design-icon.svg"
          alt="Replicate Design"
        />
      )
      bgColor = "#E7FAFF"
    } else if (
      title?.includes("Furniture Design") ||
      title?.includes("Added Furniture to Environment") ||
      title?.includes("تصميم الأثاث") ||
      title?.includes("تصميم الاثاث") ||
      title?.includes("إضافة الأثاث إلى المساحة") ||
      title?.includes("إضافة الاثاث إلى المساحة")
    ) {
      icon = (
        <img
          src="/dashboard/furniture-design-icon.svg"
          alt="Furniture Design"
        />
      )
      bgColor = "#E7FAFF"
    } else if (
      title?.includes("Wall Design") ||
      title?.includes("تصميم الجدار")
    ) {
      icon = (
        <img
          src="/dashboard/wall-design-icon.svg"
          alt="Wall Design"
        />
      )
      bgColor = "#E7FAFF"
    } else if (matchesFloorDesignTitle(title)) {
      icon = (
        <img
          src="/dashboard/floor-design-icon.svg"
          alt={formatFeatureLabel("floor_design", lang)}
        />
      )
      bgColor = "#E7FAFF"
    } else if (
      title?.includes("Room Planner") ||
      title?.includes("مخطط الغرفة")
    ) {
      icon = (
        <img
          src="/dashboard/floor-generation-icon.svg"
          alt="Room Planner"
        />
      )
      bgColor = "#E7FAFF"
    } else if (title?.includes("Signup")) {
      icon = (
        <img
          src="/dashboard/user-add-blue.svg"
          alt="Room Redesign"
        />
      )
      bgColor = "#E4EEFE"
    } else if (title?.includes("Plan")) {
      icon = <img src="/dashboard/crown-orang.svg" />
      bgColor = "#FFF4EE"
    } else {
      icon = <StarOutlined />
    }

    return { icon, bgColor }
  }

  const processedImages = (
    userDetails?.furniture_finder?.recent_images || []
  ).filter((img) => img && img.trim() !== "")

  // 🔹 Helper: Extract S3 key from image URL
  const extractS3Key = (imageUrl) => {
    if (!imageUrl) return null
    try {
      const url = new URL(imageUrl)
      // Remove leading slash from pathname
      return url.pathname.substring(1)
    } catch (err) {
      console.error("Error extracting S3 key:", err)
      return null
    }
  }

  // 🔹 Handle image click - fetch furniture JSON and open modal
  const handleImageClick = async (imageUrl) => {
    try {
      setLoading(true)
      const key = extractS3Key(imageUrl)
      if (!key) {
        message.error(
          lang === "ar"
            ? "تعذر استخراج مفتاح الصورة"
            : "Failed to extract image key"
        )
        return
      }

      const res = await Analytics.getFurnitureJson(key)
      // Handle response structure: { success: true, result: { json: { detected_objects: [...], frame_size: [...] } } }
      const responseData = res?.data || res || {}

      // Extract the json data from result.json
      const jsonData =
        responseData?.result?.json || responseData?.json || responseData || {}

      // The jsonData contains detected_objects and frame_size directly
      const imageJson = {
        detected_objects: jsonData.detected_objects || [],
        frame_size: jsonData.frame_size || [1920, 1080],
        ...jsonData, // Include all other properties from json
      }

      // Format data exactly like timeline view (matching getAiFeatureDetail structure)
      const detail = {
        input: imageUrl,
        status: responseData.status || jsonData.status || "success",
        image_json: imageJson,
        date: responseData.date || jsonData.date || "",
        time: responseData.time || jsonData.time || "",
        reason: responseData.reason || jsonData.reason || "",
      }

      setSelectedTimelineItem({
        title: "Used Furniture Finder",
        date: responseData.date || new Date().toLocaleDateString(),
        detail,
      })

      setFurnitureModal(true)
    } catch (err) {
      console.error("❌ Error fetching furniture JSON:", err)
      message.error(
        lang === "ar"
          ? "فشل في تحميل بيانات الصورة"
          : "Failed to load image data"
      )
    } finally {
      setLoading(false)
    }
  }

  const recentGenerations = useMemo(() => {
    const fromApi = parseRecentGenerations(
      userDetails?.recent_generations,
      userDetails?.recent_generation_times
    )
    const fromTimeline = buildRecentGenerationStubsFromTimeline(
      timelineItems,
      lang
    )
    return applyTimelineTimestampsToRecentGenerations(
      mergeRecentGenerations(fromApi, fromTimeline),
      timelineItems
    )
  }, [
    userDetails?.recent_generations,
    userDetails?.recent_generation_times,
    timelineItems,
    lang,
  ])

  const [roomPlannerThumbs, setRoomPlannerThumbs] = useState({})
  const roomPlannerCacheRef = useRef({})
  const roomPlannerThumbRequestsRef = useRef(new Set())

  const openRoomPlannerGallery = async (
    uuid,
    fallbackRoom,
    { forceRefresh = false } = {}
  ) => {
    const cacheKey = getRoomPlannerCacheKey(uuid, fallbackRoom)
    const cached = !forceRefresh ? roomPlannerCacheRef.current[cacheKey] : null
    if (
      cached &&
      (cached.images?.length || cached.preview) &&
      roomPlannerNamesMatch(cached.room_name, fallbackRoom)
    ) {
      setSelectedFloorGeneration({
        ...cached,
        room_name: cached.room_name || fallbackRoom || null,
      })
      setFloorGenerationModal(true)
      return true
    }

    const normalized = await fetchRoomPlannerDetail(
      (id, feature) => Analytics.getAiFeatureDetail(id, feature),
      uuid,
      fallbackRoom
    )
    if (!normalized?.preview && !normalized?.images?.length) {
      message.error(
        lang === "ar"
          ? "لا توجد صور متاحة لهذا المخطط"
          : "No images available for this room planner render"
      )
      return false
    }

    const payload = {
      ...normalized,
      room_name: normalized.room_name || fallbackRoom || null,
    }
    roomPlannerCacheRef.current[cacheKey] = payload
    if (payload.preview) {
      setRoomPlannerThumbs((prev) => ({
        ...prev,
        [cacheKey]: payload.preview,
      }))
    }
    setSelectedFloorGeneration(payload)
    setFloorGenerationModal(true)
    return true
  }

  useEffect(() => {
    if (!userId) return

    let active = true
    const loadThumbnails = async () => {
      for (const entry of recentGenerations) {
        if (!active) return
        if (entry.key !== "floor_generation" || !entry.uuid) continue

        if (entry.floorGeneration?.preview || entry.floorGeneration?.images?.length) {
          const cacheKey = getRoomPlannerCacheKey(entry.uuid, entry.roomName)
          roomPlannerCacheRef.current[cacheKey] = entry.floorGeneration
          if (!entry.img && entry.floorGeneration.preview) {
            setRoomPlannerThumbs((prev) => {
              if (prev[cacheKey]) return prev
              return { ...prev, [cacheKey]: entry.floorGeneration.preview }
            })
          }
          continue
        }

        if (entry.img) continue
        const cacheKey = getRoomPlannerCacheKey(entry.uuid, entry.roomName)
        if (roomPlannerCacheRef.current[cacheKey]?.preview) continue
        if (roomPlannerThumbRequestsRef.current.has(cacheKey)) continue

        roomPlannerThumbRequestsRef.current.add(cacheKey)
        try {
          const normalized = await fetchRoomPlannerDetail(
            (uuid, feature) => Analytics.getAiFeatureDetail(uuid, feature),
            entry.uuid,
            entry.roomName
          )
          if (!active || !normalized?.preview) continue
          roomPlannerCacheRef.current[cacheKey] = normalized
          setRoomPlannerThumbs((prev) => ({
            ...prev,
            [cacheKey]: normalized.preview,
          }))
        } catch (err) {
          console.error("❌ Room planner thumbnail fetch failed:", err)
        } finally {
          roomPlannerThumbRequestsRef.current.delete(cacheKey)
        }
      }
    }

    loadThumbnails()
    return () => {
      active = false
    }
  }, [userId, recentGenerations])

  useEffect(() => {
    setRoomPlannerThumbs({})
    roomPlannerCacheRef.current = {}
    roomPlannerThumbRequestsRef.current = new Set()
  }, [userId])

  const getRecentGenerationThumb = (entry) => {
    if (entry.key !== "floor_generation") return entry.img || null
    const cacheKey = entry.uuid
      ? getRoomPlannerCacheKey(entry.uuid, entry.roomName)
      : null
    return (
      entry.img ||
      entry.floorGeneration?.preview ||
      (cacheKey ? roomPlannerCacheRef.current[cacheKey]?.preview : null) ||
      (cacheKey ? roomPlannerThumbs[cacheKey] : null) ||
      null
    )
  }

  useEffect(() => {
    setImageLoadError(false)
  }, [selectedImage])

  const featureIconMap = {
    text_to_image: "/dashboard/credits-icon.svg",
    sketch_to_render: "/dashboard/quick-edits.svg",
    object_removal: "/dashboard/object-removal-icon.svg",
    quick_edit: "/dashboard/object-removal-icon.svg",
    home_redesign: "/dashboard/room-redesign-icon.svg",
    room_redesign: "/dashboard/room-redesign-icon.svg",
    reference_style: "/dashboard/reference-style-icon.svg",
    wall_design: "/dashboard/wall-design-icon.svg",
    floor_design: "/dashboard/floor-design-icon.svg",
    floor_generation: "/dashboard/floor-generation-icon.svg",
    ai_generated: "/dashboard/credits-icon.svg",
    furniture_finder: "/dashboard/sofa-icon.svg",
    decor_transfer: "/dashboard/room-redesign-icon.svg",
    virtual_unstaging: "/dashboard/virtua_unstaging.svg",
    furniture_removal: "/dashboard/virtua_unstaging.svg",
    enhance_image: "/dashboard/image-upload.svg",
    design_with_product: "/dashboard/design-with-product-icon.svg",
    product_visualizer: "/dashboard/replicate-design-icon.svg",
    furniture_design: "/dashboard/furniture-design-icon.svg",
  }

  const getRecentGenerationFeatureIcon = (key) =>
    key === "expert_design"
      ? EVENT_BOOTH_TIMELINE_ICON
      : getFeatureDashboardIcon(key, featureIconMap[key] || "/dashboard/credits-icon.svg")
  const getRecentGenerationFeatureLabel = (key) => {
    if (key === "expert_design") {
      return lang === "ar" ? "بوث الفعاليات" : "Event Booth"
    }
    if (key === "floor_generation") {
      return lang === "ar" ? "مخطط الغرفة" : "Room Planner"
    }
    return formatFeatureLabel(key, lang)
  }

  const renderRecentGenIconBadge = (featureKey, iconSize = 18) => (
    <div
      style={{
        border: "1px solid #ACD2DC26",
        background: "#F8F8F8",
        width: iconSize + 12,
        height: iconSize + 12,
        padding: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <img
        src={getRecentGenerationFeatureIcon(featureKey)}
        alt={featureKey}
        style={{ width: iconSize, height: iconSize, objectFit: "contain" }}
      />
    </div>
  )

  const openGenerationDetailByKey = (key, detail, meta = {}) => {
    const featureKey = normalizeFeatureKey(key)
    setSelectedTimelineItem({
      title: meta.title || `Used ${getRecentGenerationFeatureLabel(featureKey)}`,
      title_arabic: meta.title_arabic,
      date: meta.date,
      featureKey,
      detail,
    })

    if (featureKey === "sketch_to_render") {
      setSketchModal(true)
      return
    }
    if (featureKey === "home_redesign" || featureKey === "room_redesign") {
      setOpenModal(true)
      return
    }
    if (featureKey === "text_to_image") {
      setTextToImgModal(true)
      return
    }
    if (featureKey === "object_removal" || featureKey === "quick_edit") {
      setQuickEditsToImgModal(true)
      return
    }
    if (featureKey === "furniture_finder") {
      setFurnitureModal(true)
      return
    }
    if (featureKey === "furniture_removal" || featureKey === "virtual_unstaging") {
      setVirtualUnstagingModal(true)
      return
    }
    if (
      featureKey === "reference_style" ||
      featureKey === "wall_design" ||
      featureKey === "floor_design" ||
      featureKey === "furniture_replace" ||
      featureKey === "rearrange_furniture" ||
      featureKey === "enhance_image" ||
      featureKey === "expert_design" ||
      featureKey === "design_with_product" ||
      featureKey === "product_visualizer" ||
      featureKey === "furniture_design"
    ) {
      setInputOutputFeatureModal(true)
      return
    }

    const output =
      detail?.output ||
      detail?.list?.[0]?.output ||
      detail?.input ||
      null
    if (output) {
      setSelectedImageKey(featureKey)
      setSelectedImage(output)
      setSelectedImageLabel(getRecentGenerationFeatureLabel(featureKey))
      setImageModal(true)
    }
  }

  const resolveAiFeatureDetailParam = (key) => {
    const featureKey = normalizeFeatureKey(key)
    if (featureKey === "expert_design") return "expert_design"
    if (featureKey === "furniture_finder") return null
    return featureKey || null
  }

  // 🔹 Handle Recent Generations image click
  const handleRecentGenerationClick = async (entry) => {
    const {
      key,
      img,
      floorGeneration,
      timelineItem,
      uuid,
      roomName,
    } = entry

    if (key === "floor_generation") {
      const cacheKey = uuid ? getRoomPlannerCacheKey(uuid, roomName) : null
      if (uuid) {
        const cached =
          (cacheKey && roomPlannerCacheRef.current[cacheKey]) ||
          (floorGeneration?.preview || floorGeneration?.images?.length
            ? floorGeneration
            : null)
        if (
          cached &&
          roomPlannerNamesMatch(cached.room_name, roomName)
        ) {
          setSelectedFloorGeneration({
            ...cached,
            room_name: cached.room_name || roomName || null,
          })
          setFloorGenerationModal(true)
          return
        }
        try {
          setLoading(true)
          await openRoomPlannerGallery(uuid, roomName, { forceRefresh: true })
        } finally {
          setLoading(false)
        }
        return
      }
      if (floorGeneration?.preview || floorGeneration?.images?.length) {
        setSelectedFloorGeneration(floorGeneration)
        setFloorGenerationModal(true)
        return
      }
    }

    if (uuid) {
      try {
        setLoading(true)
        const res = await Analytics.getAiFeatureDetail(
          uuid,
          resolveAiFeatureDetailParam(key)
        )
        const detail = res?.data ||
          res || {
            status: "unknown",
            input: "",
            output: "",
          }

        openGenerationDetailByKey(key, detail, {
          title: timelineItem?.title,
          title_arabic: timelineItem?.title_arabic,
          date: timelineItem?.date,
        })
      } catch (err) {
        console.error("❌ Error fetching generation detail:", err)
        message.error(
          lang === "ar"
            ? "فشل في تحميل تفاصيل الإنشاء"
            : "Failed to load generation details"
        )
      } finally {
        setLoading(false)
      }
      return
    }

    if (key === "furniture_finder") {
      if (!img) return
      await handleImageClick(img)
      return
    }

    if (!img) return

    setSelectedImageKey(key)
    setSelectedImage(img)
    setSelectedImageLabel(getRecentGenerationFeatureLabel(key))
    setImageModal(true)
  }

  // 🧠 Apply Filters (date only — activity type is filtered server-side)
  const filteredTimeline = timelineItems.filter((item) => {
    // Filter by date range
    if (customRange?.startDate && customRange?.endDate) {
      const itemDate = dayjs(item.date)
      if (
        itemDate.isBefore(dayjs(customRange.startDate)) ||
        itemDate.isAfter(dayjs(customRange.endDate))
      )
        return false
    }

    return true
  })

  // 🔹 Converts ISO datetime → readable format
  const getPlanTagStyle = (plan) => {
    // Normalize Arabic to English for consistent mapping
    const normalizedPlan =
      plan === "فليكس" || plan === "Flex"
        ? "Flex"
        : plan === "التجربة المجانية" || plan === "Free Trial"
        ? "Free Trial"
        : plan === "سجنتر" || plan === "Signature"
        ? "Signature"
        : plan === "ادفانس" || plan === "Advanced"
        ? "Advanced"
        : plan === "بريميوم" || plan === "Premium"
        ? "Premium"
        : plan

    switch (normalizedPlan) {
      case "Flex":
        return {
          color: "#0A379A",
          backgroundColor: "#EAF1FF",
          borderColor: "#8EB2FF",
        }
      case "Signature":
        return {
          color: "#774BCF",
          backgroundColor: "#F4F0FF",
          borderColor: "#D9D6FE",
        }
      case "Advanced":
        return {
          color: "#B93815",
          backgroundColor: "#FEF6EE",
          borderColor: "#F9DBAF",
        }
      case "Premium":
        return {
          color: "#C11574",
          backgroundColor: "#FDF2FA",
          borderColor: "#FCCEEE",
        }
      case "Free Trial":
        return {
          color: "#101828",
          backgroundColor: "#F9FAFB",
          borderColor: "#E5E7EB",
        }
      default:
        return {
          color: "#344054",
          backgroundColor: "#F9FAFB",
          borderColor: "#E5E7EB",
        }
    }
  }

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A"
    const date = new Date(isoString)

    // Format: 14 Oct 2025, 8:08 PM
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <div
      style={{
        // backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <div className="container-fluid">
        {/* Breadcrumb */}
        <Breadcrumb style={{ marginBottom: "24px" }}>
          <Breadcrumb.Item>
            <span
              style={{ cursor: "pointer", color: "#1890ff" }}
              onClick={() => {
                if (onBack) {
                  onBack()
                } else {
                  navigate("/all-users")
                }
              }}
            >
              {lang === "ar" ? "لوحة التحكم" : "Dashboard"}
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span
              style={{ cursor: "pointer", color: "#1890ff" }}
              onClick={() => {
                if (onBack) {
                  onBack()
                } else {
                  navigate("/all-users")
                }
              }}
            >
              {lang === "ar" ? "المستخدمون" : "Users"}
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            {userDetails?.name || "User Details"}
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Header */}
        <div
          className="card"
          style={{ border: "none", borderRadius: "8px", background: "#f5f5f5" }}
        >
          <div
            className=""
            style={{ background: "#f5f5f5", paddingBottom: "20px" }}
          >
            <div className="d-flex align-items-center justify-content-between w-100">
              {/* Left */}
              <div className="d-flex align-items-center">
                <Avatar
                  size={64}
                  src={
                    userDetails?.profile_image &&
                    userDetails.profile_image.trim() !== ""
                      ? userDetails.profile_image
                      : null
                  }
                  style={{
                    backgroundColor:
                      !userDetails?.profile_image ||
                      userDetails?.profile_image?.trim() === ""
                        ? "#1890ff"
                        : "transparent",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {(!userDetails?.profile_image ||
                    userDetails?.profile_image?.trim() === "") &&
                    (userDetails?.name
                      ? userDetails.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()
                      : "NA")}
                </Avatar>

                <div
                  className="ms-3"
                  style={{ marginRight: lang === "ar" ? "10px" : "" }}
                >
                  <h4
                    className="mb-0"
                    style={{ fontWeight: 600 }}
                  >
                    {userDetails?.name}
                  </h4>
                  <div
                    className="d-flex align-items-center gap-2 mt-1"
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (userDetails?.email) {
                        navigator.clipboard
                          .writeText(userDetails.email)
                          .then(() => {
                            message.success("Email copied to clipboard!", 1.5)
                          })
                          .catch(() => {
                            message.error("Failed to copy email")
                          })
                      }
                    }}
                  >
                    {userDetails?.email}
                    <img
                      src="/dashboard/copy_icon.svg"
                      alt="Copy"
                      style={{ width: "16px", height: "16px" }}
                    />
                  </div>

                  <div
                    className="d-flex flex-wrap gap-3 mt-1"
                    style={{ fontSize: "13px", color: "#666" }}
                  >
                    {countryInfo?.name && (
                      <span
                        className="d-flex align-items-center gap-2"
                        style={{ fontSize: "14px", color: "#444" }}
                      >
                        {countryInfo.flag && (
                          <img
                            src={countryInfo.flag}
                            alt={countryInfo.name}
                            style={{
                              width: "16px",
                              height: "16px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "1px solid #ddd",
                            }}
                          />
                        )}
                        <span>{countryInfo.name}</span>
                      </span>
                    )}
                    <span>
                      <img src="/dashboard/caleder-iconn.svg" /> Joined:
                      {userDetails?.joined}
                    </span>
                    <span>
                      {/* <img src="/dashboard/clock-01.svg" alt="clock" /> Last
                      login:{" "}
                      {formatDateTime(userDetails?.last_activity_timestamp)} */}
                    </span>
                  </div>
                </div>
              </div>
              {/* Right */}
              <div className="d-flex align-items-center gap-2">
                <Tag
                  style={{
                    ...getPlanTagStyle(
                      lang === "ar"
                        ? userDetails?.arabic_plan_name
                        : userDetails?.plan_name
                    ),
                    fontWeight: 500,
                  }}
                >
                  {lang === "ar"
                    ? userDetails?.arabic_plan_name
                    : userDetails?.plan_name}
                </Tag>
                <Tag
                  color="#ECFDF5"
                  style={{
                    color: "#004F3B",
                    borderColor: "#A4F4CF",
                    fontWeight: 500,
                  }}
                >
                  {lang === "ar"
                    ? userDetails?.is_active
                      ? "نشط"
                      : "غير نشط"
                    : userDetails?.is_active
                    ? "Active"
                    : "Inactive"}
                </Tag>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          {statCards.map((stat, idx) => (
            <div
              key={idx}
              className="col-md-3"
            >
              <div
                className="card h-100"
                style={{
                  border: "none",
                  borderRadius: "8px",
                  borderLeft: "4px solid #343C3E",
                }}
              >
                <div className="card-body d-flex flex-column justify-content-center">
                  <div style={{ color: "#666", fontSize: "14px" }}>
                    {stat.label}
                  </div>
                  <h3 className="mb-0">
                    {" "}
                    {stat.label === "Total Spendings" ||
                    stat.label === "إجمالي الإنفاق"
                      ? "SAR"
                      : ""}{" "}
                    {stat.value}
                  </h3>
                </div>
              </div>
            </div>
          ))}

          {/* Credit summary card */}
          {/* Credit summary card */}
          <div className="col-md-3">
            <div
              className="card h-100"
              style={{
                border: "none",
                borderRadius: "8px",
                borderLeft: "4px solid #343C3E",
              }}
            >
              <div className="card-body d-flex flex-column justify-content-center">
                {/* Title */}
                <div
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    marginBottom: "6px",
                  }}
                >
                  <div>{lang === "ar" ? "الملخص" : "Summary"} </div>
                </div>

                {/* Usage */}
                <div
                  className="d-flex justify-content-between align-items-center"
                  style={{ fontSize: "14px", color: "#666" }}
                >
                  <span>{lang === "ar" ? "الاستخدام" : "Usage"} </span>
                  <span style={{ fontWeight: 500, color: "#333" }}>
                    <span style={{ color: "#8c8c8c" }}>
                      {planUsedImages}
                    </span>
                    /{planTotalImages}
                  </span>
                </div>

                {/* Progress bar */}
                <Progress
                  percent={planUsagePercent}
                  strokeColor="#4A90E2"
                  showInfo={false}
                />

                {/* Description */}
                <small style={{ color: "#666" }}>
                  {planUsagePercent.toFixed(1)}%{" "}
                  {lang === "ar"
                    ? "إجمالي الصور المستهلكة"
                    : "of total images consumed"}
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Usage */}
        <h5 className="mb-3">
          {lang === "ar" ? "الميزات المستخدمة" : "Feature Usage"}{" "}
        </h5>
        <div className="row g-3 mb-4">
          {/* Furniture Finder */}
          <div className="col-md-6">
            <div
              className="card"
              style={{ border: "none", borderRadius: "8px" }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="feature-usage-icon">
                    <img
                      src="/dashboard/sofa-icon.svg"
                      alt=""
                    />
                  </div>
                  <div style={{ paddingLeft: "10px" }}>
                    <h6 className="mb-0">
                      {lang === "ar" ? "مُستكشف الأثاث" : "Furniture Finder"}
                    </h6>
                    <small style={{ color: "#666" }}>
                      {lang === "ar"
                        ? "اكتشاف الكائنات المدعوم بالذكاء الاصطناعي"
                        : "AI-powered object detection"}
                    </small>
                  </div>
                </div>

                {/* Image Stats */}
                <div className="row">
                  <div className="col-6">
                    <div
                      style={{
                        background: "#f8f8f8",
                        padding: "12px",
                        borderRadius: "8px",
                      }}
                    >
                      <h6 className="mb-0">
                        {userDetails?.furniture_finder?.total_processing_images}
                      </h6>
                      <small style={{ color: "#666" }}>
                        {lang === "ar" ? "الصور المعالجة" : "Images Processed"}
                      </small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div
                      style={{
                        background: "#f8f8f8",
                        padding: "12px",
                        borderRadius: "8px",
                      }}
                    >
                      <h6 className="mb-0">
                        {userDetails?.furniture_finder?.last_used}
                      </h6>
                      <small style={{ color: "#666" }}>
                        {lang === "ar" ? "آخر استخدام" : "Last Used"}
                      </small>
                    </div>
                  </div>
                </div>

                {/* Processed Images */}
                {/* Processed Images */}
                <div className="mt-3">
                  <h6 style={{ fontSize: "14px", marginBottom: "8px" }}>
                    {lang === "ar"
                      ? "أحدث الصور المعالجة"
                      : "Recent Processed Images"}
                  </h6>
                  <div
                    className="d-flex flex-wrap justify-content-start gap-2"
                    style={{
                      rowGap: "12px",
                      columnGap: "12px",
                    }}
                  >
                    {processedImages.length > 0 ? (
                      processedImages
                        .filter((img) => img && img.trim() !== "")
                        .map((img, idx) => (
                          <div
                            key={idx}
                            style={{
                              flex: "1 1 calc(25% - 12px)",
                              maxWidth: "calc(25% - 12px)",
                              cursor: "pointer",
                            }}
                            className="responsive-img-wrapper"
                            onClick={() => handleImageClick(img)}
                          >
                            <img
                              src={img}
                              alt={`Processed ${idx}`}
                              style={{
                                width: "100%",
                                height: "auto",
                                borderRadius: "8px",
                                objectFit: "cover",
                                aspectRatio: "1 / 1",
                                transition: "opacity 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = "0.8"
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = "1"
                              }}
                            />
                          </div>
                        ))
                    ) : (
                      <p style={{ color: "#888", fontSize: "14px" }}>
                        No images found
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Distribution */}
          <div className="col-md-6">
            <div
              className="card"
              style={{ border: "none", borderRadius: "8px" }}
            >
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="feature-usage-icon">
                    <img
                      src="/dashboard/crown.svg"
                      alt=""
                    />
                  </div>
                  <div style={{ paddingLeft: "10px" }}>
                    <h6 className="mb-0">
                      {lang === "ar"
                        ? "توزيع الميزات"
                        : "Features Distribution"}
                    </h6>
                    <small style={{ color: "#666" }}>
                      {lang === "ar"
                        ? "توزيع الميزات حسب الصور المُنشأة"
                        : "Features distribution by generated images"}
                    </small>
                  </div>
                </div>

                {/* Blue Gradient Bar (FIGMA STYLE) */}
                {/* <div
                  style={{
                    position: "relative",
                    height: "40px",
                    borderRadius: "20px",
                    overflow: "hidden",
                    background:
                      "linear-gradient(90deg, #5b8def 0%, #4169e1 50%, #1e3a8a 100%)",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "white",
                      fontSize: "13px",
                      fontWeight: "600",
                      textAlign: "right",
                      lineHeight: "1.2",
                    }}
                  >
                    Room Redesign
                    <br />
                    8,920 | 44.3%
                  </div>
                </div> */}

                <div>
                  <BarContainer
                    generatedImagesPercent={userDetails?.generated_images_percent}
                    generatedImages={
                      generatedImages.byFeature || userDetails?.generated_images
                    }
                  />
                </div>

                <div
                  style={{
                    textAlign: "center",
                    fontSize: "14px",
                    color: "#666",
                    paddingTop: "10px",
                  }}
                >
                  {lang === "ar" ? "الصور المُنشأة" : "Images generated"}:{" "}
                  <strong>{totalGeneratedImages.toLocaleString()}</strong>
                </div>

                {/* Recent Generations */}
                <div className="mt-3">
                  <h6 style={{ fontSize: "14px", marginBottom: "8px" }}>
                    {lang === "ar" ? "أحدث الإنشاءات" : "Recent Generations"}
                  </h6>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      overflowX: "auto",
                      overflowY: "hidden",
                      paddingBottom: "4px",
                    }}
                  >
                    {recentGenerations.length > 0 ? (
                      recentGenerations.map((entry, idx) => {
                        const thumbSrc = getRecentGenerationThumb(entry)
                        return (
                        <div
                          key={`${entry.key}-${entry.uuid || entry.img || idx}`}
                          className="responsive-img-wrapper"
                          style={{
                            position: "relative",
                            borderRadius: "8px",
                            overflow: "hidden",
                            cursor: "pointer",
                            flex: "0 0 calc((100% - 36px) / 4)",
                            minWidth: "calc((100% - 36px) / 4)",
                          }}
                          onClick={() => handleRecentGenerationClick(entry)}
                        >
                          {thumbSrc ? (
                            <img
                              src={thumbSrc}
                              alt={getRecentGenerationFeatureLabel(entry.key)}
                              style={{
                                width: "100%",
                                height: "auto",
                                borderRadius: "8px",
                                transition: "opacity 0.2s",
                                ...(entry.key === "floor_generation"
                                  ? getRoomPlannerThumbnailLayout("dollhouse_front")
                                      .imageStyle
                                  : {
                                      objectFit: "cover",
                                      aspectRatio: "1 / 1",
                                    }),
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = "0.8"
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = "1"
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                borderRadius: "8px",
                                background: "#f0f2f5",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "10px",
                                padding: "12px",
                              }}
                            >
                              {renderRecentGenIconBadge(entry.key, 20)}
                              {entry.roomName && (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    textAlign: "center",
                                    lineHeight: 1.3,
                                    fontWeight: 500,
                                  }}
                                >
                                  {entry.roomName}
                                </span>
                              )}
                            </div>
                          )}

                          <div
                            title={getRecentGenerationFeatureLabel(entry.key)}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                            }}
                          >
                            {renderRecentGenIconBadge(entry.key, 16)}
                          </div>
                        </div>
                        )
                      })
                    ) : (
                      <p style={{ color: "#888", fontSize: "14px" }}>
                        No recent generations
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription History */}
        {subResults.length > 0 && (
            <>
              <h5 className="mb-3">
                {lang === "ar" ? "سجل الاشتراكات" : "Subscription History"}
              </h5>
              <div
                className="card mb-4"
                style={{ border: "none", borderRadius: "8px" }}
              >
                <div className="card-body" style={{ position: "relative" }}>
                  {subLoading && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(255,255,255,0.6)",
                        zIndex: 2,
                        borderRadius: "8px",
                      }}
                    >
                      <Spin />
                    </div>
                  )}
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead>
                        <tr style={{ color: "#667085", fontSize: "13px" }}>
                          <th>{lang === "ar" ? "الباقة" : "Plan"}</th>
                          <th>{lang === "ar" ? "المدة" : "Duration"}</th>
                          <th>{lang === "ar" ? "المبلغ" : "Amount"}</th>
                          <th>
                            {lang === "ar" ? "كود الخصم" : "Discount Code"}
                          </th>
                          <th>{lang === "ar" ? "طريقة الدفع" : "Payment"}</th>
                          <th>{lang === "ar" ? "الحالة" : "Status"}</th>
                          <th>{lang === "ar" ? "الفترة" : "Period"}</th>
                          <th>
                            {lang === "ar" ? "التجديد التلقائي" : "Auto Renew"}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {subResults.map((sub, idx) => {
                            const PAYMENT_LABELS = {
                              CARD: lang === "ar" ? "بطاقة" : "Card",
                              APPLEPAY: "Apple Pay",
                              TAMARA: lang === "ar" ? "تمارا" : "Tamara",
                            }
                            const payment = sub.payment_method
                              ? PAYMENT_LABELS[sub.payment_method] ||
                                sub.payment_method
                              : "—"
                            const STATUS_LABELS_AR = {
                              Completed: "مكتملة",
                              Pending: "قيد الانتظار",
                              Failed: "فشلت",
                              Cancelled: "ملغاة",
                              Canceled: "ملغاة",
                            }
                            const statusLabel =
                              lang === "ar"
                                ? STATUS_LABELS_AR[sub.status] || sub.status
                                : sub.status
                            const duration =
                              sub.plan_duration === "Yearly"
                                ? lang === "ar"
                                  ? "سنوي"
                                  : "Yearly"
                                : lang === "ar"
                                ? "شهري"
                                : "Monthly"
                            return (
                              <tr key={idx} style={{ fontSize: "13px" }}>
                                <td>
                                  {lang === "ar"
                                    ? sub.plan_name_arabic
                                    : sub.plan_name}
                                </td>
                                <td>{duration}</td>
                                <td>
                                  {sub.paid_amount} {sub.currency}
                                </td>
                                <td>
                                  {sub.discount_applied && sub.discount_code
                                    ? sub.discount_code
                                    : "—"}
                                </td>
                                <td>
                                  {payment}
                                  {sub.payment_brand
                                    ? ` · ${sub.payment_brand}`
                                    : ""}
                                </td>
                                <td>
                                  {sub.is_active ? (
                                    <Tag color="green">
                                      {lang === "ar" ? "نشط" : "Active"}
                                    </Tag>
                                  ) : (
                                    <Tag>{statusLabel}</Tag>
                                  )}
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {sub.subscription_start} — {sub.subscription_end}
                                </td>
                                <td>
                                  {sub.auto_renew
                                    ? lang === "ar"
                                      ? "نعم"
                                      : "Yes"
                                    : lang === "ar"
                                    ? "لا"
                                    : "No"}
                                </td>
                              </tr>
                            )
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                  {subTotal > SUB_PAGE_SIZE && (
                    <div className="d-flex justify-content-end mt-3">
                      <Pagination
                        current={subPage}
                        pageSize={SUB_PAGE_SIZE}
                        total={subTotal}
                        onChange={(p) => fetchSubscriptions(p)}
                        showSizeChanger={false}
                        size="small"
                        disabled={subLoading}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

        {/* User Timeline */}
        {/* 🧾 Timeline */}
        <div
          className=" mt-4"
          style={{ border: "none", borderRadius: "8px" }}
        >
          <div className=" p-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">
                {lang === "ar" ? "الجدول الزمني للمستخدم" : "User Timeline"}
              </h5>

              {/* Filters */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                {" "}
                <div className="d-flex gap-2 align-items-center">
                  <Dropdown
                    overlay={activityMenu}
                    trigger={["click"]}
                  >
                    <Button
                      style={{
                        height: "38px",
                        fontSize: "14px",
                        fontWeight: 500,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "6px",
                      }}
                    >
                      {activityTranslations[lang][selectedActivityKey]}
                      <img
                        src="/dashboard/Vector-down.svg"
                        alt="down"
                      />
                    </Button>
                  </Dropdown>

                  <Dropdown
                    overlay={dateMenu}
                    trigger={["click"]}
                  >
                    <Button
                      style={{
                        height: "38px",
                        fontSize: "14px",
                        fontWeight: 500,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "6px",
                      }}
                    >
                      {dateTranslations[lang][selectedDateKey]}
                      <img
                        src="/dashboard/Vector-down.svg"
                        alt="down"
                      />
                    </Button>
                  </Dropdown>

                  {showDatePicker && (
                    <RangePicker
                      allowClear={false}
                      disabledDate={(current) => {
                        // ✅ Disable future dates (after today)
                        return current && current > dayjs().endOf("day")
                      }}
                      onChange={(dates) => {
                        if (dates?.length === 2) {
                          setCustomRange({
                            startDate: dates[0].format("YYYY-MM-DD"),
                            endDate: dates[1].format("YYYY-MM-DD"),
                          })
                          setPage(1)
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div
              className=" card-body"
              style={{
                background: "#ffffff",
                padding: "20px",
                border: "1px solid #E9EAEB",
                borderRadius: "16px",
              }}
            >
              {timelineLoading ? (
                <div className="text-center py-5">
                  <Spin size="large" />
                </div>
              ) : filteredTimeline.length === 0 ? (
                <p className="text-center text-muted">No timeline data found</p>
              ) : (
                filteredTimeline.map((item, idx) => (
                  <div
                    key={idx}
                    className="d-flex gap-3 mb-3 p-2"
                    style={{
                      border: "1px solid #f0f0f0",
                      borderRadius: "8px",
                    }}
                  >
                    {(() => {
                      const { icon, bgColor } = getIconWithBg(item)
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center", // ✅ Vertically center the 40x40 box
                          }}
                        >
                          <div
                            className="user-timeline-activity-icon"
                            style={{
                              width: "40px",
                              height: "40px",
                              minWidth: "40px",
                              maxWidth: "40px",
                              minHeight: "40px",
                              maxHeight: "40px",
                              borderRadius: "8px",
                              backgroundColor: bgColor,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              boxSizing: "border-box",
                            }}
                          >
                            {icon}
                          </div>
                        </div>
                      )
                    })()}

                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between ">
                        <div>
                          <h6 className="mb-1">
                            {lang === "ar" ? item?.title_arabic : item.title}
                          </h6>
                          <p
                            className="mb-0"
                            style={{ color: "#666", fontSize: "14px" }}
                          >
                            {lang === "ar" ? item?.detail_arabic : item.detail}
                          </p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {item.generations > 0 && (
                            <Tag color="blue">
                              {item.generations}{" "}
                              {lang === "ar" ? "الأجيال" : "Generations"}
                            </Tag>
                          )}
                          <span style={{ color: "#999", fontSize: "14px" }}>
                            {item.date}
                          </span>
                          {!item.title
                            .toLowerCase()
                            .includes("new user signup") &&
                            item.type !== "payment" &&
                            !item.title
                              ?.toLowerCase()
                              .includes("organization plan purchased") && (
                              <Button
                                size="small"
                                type="link"
                                style={{
                                  background: "#FFF4E6",
                                  border: "1px solid #FFBC98",
                                  color: "#FB6514",
                                  borderRadius: "6px",
                                  padding: "12px 10px",
                                  fontWeight: 400,
                                  fontSize: "12px",
                                }}
                                onClick={async () => {
                                  try {
                                    setLoading(true)

                                    if (item.type === "room_planner") {
                                      const fallbackRoom =
                                        extractRoomNameFromTimelineDetail(
                                          lang === "ar"
                                            ? item.detail_arabic
                                            : item.detail
                                        )
                                      await openRoomPlannerGallery(
                                        item.uuid,
                                        fallbackRoom,
                                        { forceRefresh: true }
                                      )
                                      return
                                    }

                                    const featureKey =
                                      resolveTimelineFeatureKey(item)
                                    const res =
                                      await Analytics.getAiFeatureDetail(
                                        item.uuid,
                                        resolveAiFeatureDetailParam(featureKey)
                                      )
                                    const detail = res?.data ||
                                      res || {
                                        status: "unknown",
                                        input: "",
                                        output: "",
                                      }

                                    openGenerationDetailByKey(
                                      featureKey || resolveTimelineFeatureKey(item) || item.title,
                                      detail,
                                      {
                                        title: item.title,
                                        title_arabic: item.title_arabic,
                                        date: item.date,
                                      }
                                    )
                                  } catch (err) {
                                    console.error(
                                      "❌ Error fetching AI feature detail:",
                                      err
                                    )
                                  } finally {
                                    setLoading(false)
                                  }
                                }}
                              >
                                {lang === "ar" ? "عرض" : "View"}{" "}
                                <img
                                  src="dashboard/vector-up.svg"
                                  alt=""
                                />
                              </Button>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {totalTimeline > 1 && (
              <div className="d-flex justify-content-end mt-4">
                <ConfigProvider direction={lang === "ar" ? "rtl" : "ltr"}>
                  <Pagination
                    current={page}
                    total={totalTimeline} // ← use actual API count
                    pageSize={10} // ← backend page size
                    showSizeChanger={false}
                    onChange={(p) => setPage(p)}
                    // showTotal={(total, range) =>
                    //   `Showing ${range[0]}-${range[1]} of ${total} records`
                    // }
                  />
                </ConfigProvider>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <GenerationThreadModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        data={selectedTimelineItem}
        onImageClick={(imageUrl, label, featureKey) => {
          setSelectedImage(imageUrl)
          setSelectedImageLabel(label)
          setSelectedImageKey(featureKey || "home_redesign")
          setImageModal(true)
        }}
      />
      <SketchToImgModal
        open={sketchModal}
        onClose={() => setSketchModal(false)}
        data={selectedTimelineItem}
        onImageClick={(imageUrl, label, featureKey) => {
          setSelectedImage(imageUrl)
          setSelectedImageLabel(label)
          setSelectedImageKey(featureKey || "home_redesign")
          setImageModal(true)
        }}
      />
      <TextToImgModal
        open={textToImgModal}
        onClose={() => setTextToImgModal(false)}
        data={selectedTimelineItem}
        onImageClick={(imageUrl, label, featureKey) => {
          setSelectedImage(imageUrl)
          setSelectedImageLabel(label)
          setSelectedImageKey(featureKey || "home_redesign")
          setImageModal(true)
        }}
      />

      <QuickEditsModal
        open={quickEditsModal}
        onClose={() => setQuickEditsToImgModal(false)}
        data={selectedTimelineItem}
        onImageClick={(imageUrl, label, featureKey) => {
          setSelectedImage(imageUrl)
          setSelectedImageLabel(label)
          setSelectedImageKey(featureKey || "home_redesign")
          setImageModal(true)
        }}
      />
      <FurnitureFinderModal
        open={furnitureModal}
        onClose={() => setFurnitureModal(false)}
        data={selectedTimelineItem}
      />
      <VirtualUnstagingModal
        open={virtualUnstagingModal}
        onClose={() => setVirtualUnstagingModal(false)}
        data={selectedTimelineItem}
        onImageClick={(imageUrl, label, featureKey) => {
          setSelectedImage(imageUrl)
          setSelectedImageLabel(label)
          setSelectedImageKey(featureKey || "home_redesign")
          setImageModal(true)
        }}
      />
      <InputOutputFeatureModal
        open={inputOutputFeatureModal}
        onClose={() => setInputOutputFeatureModal(false)}
        data={selectedTimelineItem}
        onImageClick={(imageUrl, label, featureKey) => {
          setSelectedImage(imageUrl)
          setSelectedImageLabel(label)
          setSelectedImageKey(featureKey || "home_redesign")
          setImageModal(true)
        }}
      />

      {/* Simple Image Modal for Recent Generations (non-furniture-finder) */}
      <Modal
        title={
          selectedImageLabel && (
            <div className="d-flex">
              <div
                className={
                  lang === "ar" ? "r-r-modal-icon-arabic" : "r-r-modal-icon"
                }
              >
                <img
                  src={getRecentGenerationFeatureIcon(
                    selectedImageKey || "text_to_image"
                  )}
                  alt=""
                />
              </div>
              <div>
                <div className="modal-heading">{selectedImageLabel}</div>
              </div>
            </div>
          )
        }
        open={imageModal}
        onCancel={() => {
          setImageModal(false)
          setSelectedImage(null)
          setSelectedImageLabel(null)
          setSelectedImageKey(null)
        }}
        footer={null}
        width={820}
        zIndex={3000}
        bodyStyle={{
          display: "flex",
          flexDirection: "column",
          // padding: "24px",
        }}
      >
        {selectedImage && (
          <>
            <p style={{ fontStyle: "italic", marginBottom: 12, color: "#555" }}>
              {lang === "ar"
                ? "تم إنشاء الصورة بنجاح"
                : "Image generated successfully"}
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#f4f4f4",
                borderRadius: "8px",
                padding: "12px",
                minHeight: "120px",
                maxHeight: "min(75vh, 720px)",
                overflow: "auto",
              }}
            >
              {imageLoadError ? (
                <p style={{ color: "#888", fontSize: "14px" }}>
                  {lang === "ar"
                    ? "تعذر تحميل الصورة"
                    : "Unable to load image preview"}
                </p>
              ) : (
                <img
                  src={selectedImage}
                  alt="Generated Image"
                  style={getRoomPlannerModalImageStyle()}
                  onError={() => setImageLoadError(true)}
                />
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Room planner gallery (user detail — all output images) */}
      <Modal
        title={
          <div className="d-flex">
            <div
              className={
                lang === "ar" ? "r-r-modal-icon-arabic" : "r-r-modal-icon"
              }
            >
              <img src="/dashboard/floor-generation-icon.svg" alt="" />
            </div>
            <div>
              <div className="modal-heading">
                {formatFeatureLabel("room_planner", lang)}
              </div>
              {selectedFloorGeneration?.room_name && (
                <div style={{ fontSize: "13px", color: "#666" }}>
                  {selectedFloorGeneration.room_name}
                </div>
              )}
            </div>
          </div>
        }
        open={floorGenerationModal}
        onCancel={() => {
          setFloorGenerationModal(false)
          setSelectedFloorGeneration(null)
        }}
        footer={null}
        width={820}
        zIndex={3000}
        bodyStyle={{
          maxHeight: "75vh",
          overflowY: "auto",
        }}
      >
        {Array.isArray(selectedFloorGeneration?.images) &&
        selectedFloorGeneration.images.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                selectedFloorGeneration.images.length >= 3
                  ? "repeat(3, minmax(0, 1fr))"
                  : `repeat(${selectedFloorGeneration.images.length}, minmax(0, 1fr))`,
              gap: 12,
            }}
          >
            {selectedFloorGeneration.images.map((image, outIdx) => {
              const keyedLabel = formatFloorGenerationImageKey(image.key, lang)
              const imageLabel =
                keyedLabel ||
                formatRoomPlannerOutputLabel(outIdx, lang)
              const layout = getRoomPlannerModalGridLayout(image.key)
              return (
                <div
                  key={`${image.key}-${outIdx}`}
                  style={
                    layout.wide ? { gridColumn: "1 / -1" } : undefined
                  }
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#667085",
                      marginBottom: 8,
                    }}
                  >
                    {imageLabel}
                  </div>
                  <img
                    src={image.url}
                    alt={imageLabel}
                    onClick={() => {
                      setSelectedImage(image.url)
                      setSelectedImageLabel(imageLabel)
                      setSelectedImageKey("floor_generation")
                      setImageModal(true)
                    }}
                    style={{
                      ...layout.imageStyle,
                      cursor: "pointer",
                    }}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          selectedFloorGeneration?.preview && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#f4f4f4",
                borderRadius: "8px",
                padding: "8px",
                minHeight: "200px",
              }}
            >
              <img
                src={selectedFloorGeneration.preview}
                alt={formatFeatureLabel("room_planner", lang)}
                style={getRoomPlannerModalImageStyle()}
              />
            </div>
          )
        )}
      </Modal>
    </div>
  )
}

export default UserDashboard
